<template>
  <PageHeader
    title="Appointment Invites"
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: 'Engagement' },
      { label: 'Appointment Invites' }
    ]"
  >
    <template #right>
      <div class="flex items-center gap-2">
        <DatePicker
          v-model="dateRange"
          selection-mode="range"
          show-presets
          class="min-w-60"
          :presets="dateRangePresets"
          :months-visible="useBreakPoints({ xs: 1, sm: 2 })"
          @select="fetchInvites"
        />
      </div>
    </template>
  </PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="AppointmentInvites"
      :value="fetchedInvites"
      :loading="isLoading"
      removable-sort
      sort-field="id"
      :sort-order="-1"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-reports-appointment-invites"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :global-filter-fields="['id', 'dealershipId']"
    >
      <template #empty>
        <Empty
          v-if="!isLoading"
          title="No Data"
        />
      </template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
      </template>

      <Column
        field="id"
        header="ID"
        hidden
      />

      <Column
        field="dealershipId"
        header="Dealership"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          {{ findDealership(data.dealershipId).name }}
        </template>
        <template #filter="{ filterModel }">
          <Select
            v-model="filterModel.value"
            :options="dealershipsList"
            searchable
            mode="multiple"
            chips
            value-key="value"
            label-key="label"
            placeholder="All"
            class="min-w-64"
          ></Select>
        </template>
      </Column>

      <Column
        field="agreementId"
        header="agreementId"
        hidden
      />

      <Column
        field="customerName"
        header="Customer"
      >
        <template #body="{ data }">
          <router-link
            :to="'/agreement/' + data.agreementId"
            class="link"
          >
            {{ data.customerName || data.agreementId }}
          </router-link>
        </template>
      </Column>

      <Column
        field="status"
        header="Status"
      >
        <template #body="{ data }">
          <div class="capitalize">
            {{ data.status }}
          </div>
        </template>
      </Column>

      <Column
        field="appointmentDate"
        header="Appointment"
      >
        <template #body="{ data }">
          <router-link :to="'/agreement/' + data.agreementId + '/appointments/' + data.appointmentId">
            <div
              v-if="data.appointmentDate"
              class="link"
            >
              {{ dayjs(data.appointmentDate).format('ll') }}
            </div>
            <div
              v-else-if="data.appointmentId"
              class="link"
            >
              {{ data.appointmentId }}
            </div>
          </router-link>
        </template>
      </Column>

      <Column
        field="createdAt"
        header="Date Created"
      >
        <template #body="{ data }">
          <div v-if="data.createdAt">
            {{ dayjs(data.createdAt).from(dayjs()) }}
          </div>
        </template>
      </Column>

      <Column
        field="createdBy.id"
        header="Created By"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.createdBy"
            size="sm"
            :avatar="data.createdBy.profilePicUrl"
            :avatar-text="data.createdBy.initials"
          >
            {{ data.createdBy.forename + ' ' + data.createdBy.surname }}
          </Chip>
        </template>
        <template #filter="{ filterModel }">
          <Select
            v-model="filterModel.value"
            :options="usersList"
            searchable
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="All"
            class="min-w-64"
          >
            <template #option="slotProps">
              <div class="flex items-center">
                <Avatar
                  :url="slotProps.option.profilePicUrl"
                  :text="slotProps.option.initials"
                  size="sm"
                  class="mr-3"
                />
                {{ slotProps.option.label }}
              </div>
            </template>
          </Select>
        </template>
      </Column>
    </DataTable>
  </Card>
</template>

<script setup>
import { ref, computed } from 'vue'

import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Button from '@/components/button/Button.vue'
import Input from '@/components/forms/Input.vue'
import Chip from '@/components/chip/Chip.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import Empty from '@/components/empty/Empty.vue'
import Select from '@/components/forms/Select.vue'
import Avatar from '@/components/avatar/Avatar.vue'

import useApiRequest from '@/composables/useApiRequest'
// utilities
import { alertToast } from '@/utilities/notification'
import { isEmptyObject } from '@/utilities'

// stores
import { useUserStore } from '@/stores/UserStore.ts'
import dayjs from 'dayjs'
import { useBreakPoints } from '@/composables/useWindowSize'

import { FilterMatchMode } from 'primevue/api'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { useRoute, useRouter } from 'vue-router'

const invites = ref(null)
const invitesApiRequest = useApiRequest()
const isLoading = invitesApiRequest.isLoading

const route = useRoute()
const router = useRouter()

const userStore = useUserStore()
const dealershipsAllowed = ref(userStore.accessAllowed.dealerships)
const dealershipsAllowedString = dealershipsAllowed.value.map(d => {
  return d.id
})

const dealershipsSelected = ref([])

const dateRange = ref([dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')])
const dateRangePresets = [
  {
    label: 'This Week',
    value: {
      start: dayjs().startOf('week'),
      end: dayjs().endOf('week')
    }
  },
  {
    label: 'This Month',
    value: {
      start: dayjs().startOf('month'),
      end: dayjs().endOf('month')
    }
  },
  {
    label: 'Last Month',
    value: {
      start: dayjs().subtract(1, 'month').startOf('month'),
      end: dayjs().subtract(1, 'month').endOf('month')
    }
  }
]

// const users = ref([])
// const usersRequest = useApiRequest()

const dealershipsList = computed(() => {
  return dealershipsAllowed.value.map(d => {
    return {
      value: d.id,
      label: d.name
    }
  })
})

const filters = ref([])

const findDealership = id => {
  const dealership = dealershipsAllowed.value.find(d => d.id == id)
  if (dealership) {
    return dealership
  }
}

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    appointmentDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dealershipId: { value: null, matchMode: FilterMatchMode.IN },
    hasAppointment: { value: null, matchMode: FilterMatchMode.EQUALS },
    'createdBy.id': { value: null, matchMode: FilterMatchMode.EQUALS }
  }
}
initFilters()

function setRouteFilters() {
  if (route.query && !isEmptyObject(route.query)) {
    clearFilters()
    for (const [key, value] of Object.entries(route.query)) {
      if (key === 'dealership') {
        dealershipsSelected.value = value.split(',').map(Number)
        fetchInvites()
      }
      if (key === 'appointment') {
        filters.value.hasAppointment.value = true
      }
    }
  }
  // Remove url query params  using router replace
  router.replace({ query: {} })
}

setRouteFilters()

function clearFilters() {
  // remove from session storage
  sessionStorage.removeItem('dt-reports-appointment-invites')
  initFilters()
}

function fetchInvites() {
  if (!dateRange.value[0] || !dateRange.value[1]) {
    return
  }
  try {
    invitesApiRequest
      .send({
        endpoint: '/v1/appointment-invites',
        params: {
          dealerships: dealershipsAllowedString.join(','),
          start: dayjs(dateRange.value[0]).format('YYYY-MM-DD'),
          end: dayjs(dateRange.value[1]).format('YYYY-MM-DD')
        }
      })
      .then(response => {
        if (response.success) {
          invites.value = response.data
        } else {
          alertToast('Failed to fetch invites', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch invites', err.message, 'error')
  }
}

fetchInvites()

const fetchedInvites = computed(() => {
  return invites.value?.map(invite => {
    return { ...invite, hasAppointment: invite.appointmentId ? true : false }
  })
})

const usersList = computed(() => {
  if (!invites.value) return []

  return Object.values(
    invites.value.reduce((acc, item) => {
      const creator = item.createdBy
      if (!acc[creator.id]) {
        acc[creator.id] = {
          ...creator,
          value: creator.id,
          label: creator.forename + ' ' + creator.surname
        }
      }
      return acc
    }, {})
  )
})
</script>

<style></style>
